import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronRight, ChevronLeft, Check, Lock, CreditCard, User, Building2, AlertCircle } from 'lucide-react';
import axios from 'axios';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import './PortalSignup.css';

const API = process.env.REACT_APP_BACKEND_URL;

const PLANS = [
  {
    id: 'advisory_30',
    name: 'Advisory Retainer: 30 Min',
    price: '$250',
    period: '/month',
    features: ['One 30-minute advisory session per month', 'Written follow-up & recommendations', 'R6 People Strategy Suite access', 'Email support between sessions'],
  },
  {
    id: 'advisory_60',
    name: 'Advisory Retainer: 60 Min',
    price: '$400',
    period: '/month',
    features: ['One 60-minute advisory session per month', 'Priority scheduling & response', 'Custom deliverables & templates', 'Full People Strategy Suite access', 'Dedicated coach assignment'],
    popular: true,
  },
];

const PortalSignup = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [selectedPlan, setSelectedPlan] = useState('advisory_30');
  const [formData, setFormData] = useState({ firstName: '', lastName: '', email: '', company: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Check for cancelled return from Stripe
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('cancelled') === 'true') {
      setStep(2);
      setError('Payment was cancelled. You can try again when ready.');
      window.history.replaceState({}, '', '/portal/signup');
    }
  }, []);

  const handleInputChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
  };

  const handleCheckout = async () => {
    if (!formData.firstName.trim() || !formData.lastName.trim() || !formData.email.trim() || !formData.company.trim()) {
      setError('Please fill in all required fields.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.post(`${API}/api/stripe/checkout`, {
        first_name: formData.firstName.trim(),
        last_name: formData.lastName.trim(),
        email: formData.email.trim(),
        company: formData.company.trim(),
        plan: selectedPlan,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      const msg = err.response?.data?.detail || 'Something went wrong. Please try again.';
      setError(msg);
      setLoading(false);
    }
  };

  return (
    <div className="signup-page" data-testid="portal-signup">
      <Header />
      <div className="signup-container">
        <div className="signup-card">
          {/* Progress Steps */}
          <div className="signup-progress" data-testid="signup-progress">
            {['Select Plan', 'Your Details', 'Payment'].map((label, i) => (
              <div key={i} className={`signup-step-dot ${step > i + 1 ? 'completed' : ''} ${step === i + 1 ? 'active' : ''}`}>
                <div className="signup-dot">{step > i + 1 ? <Check size={14} /> : i + 1}</div>
                <span>{label}</span>
              </div>
            ))}
          </div>

          {/* Step 1: Select Plan */}
          {step === 1 && (
            <div className="signup-step-content" data-testid="step-plan">
              <h2>Choose Your Advisory Plan</h2>
              <p className="signup-subtitle">Monthly subscription. Cancel anytime.</p>
              <div className="signup-plans">
                {PLANS.map(plan => (
                  <button
                    key={plan.id}
                    className={`signup-plan-card ${selectedPlan === plan.id ? 'selected' : ''} ${plan.popular ? 'popular' : ''}`}
                    onClick={() => setSelectedPlan(plan.id)}
                    data-testid={`plan-${plan.id}`}
                  >
                    {plan.popular && <span className="signup-plan-badge">Most Popular</span>}
                    <h3>{plan.name}</h3>
                    <div className="signup-plan-price">
                      <span className="signup-price">{plan.price}</span>
                      <span className="signup-period">{plan.period}</span>
                    </div>
                    <ul>
                      {plan.features.map((f, i) => (
                        <li key={i}><Check size={14} /> {f}</li>
                      ))}
                    </ul>
                    <div className="signup-plan-radio">
                      <div className={`signup-radio ${selectedPlan === plan.id ? 'checked' : ''}`} />
                    </div>
                  </button>
                ))}
              </div>
              <div className="signup-actions">
                <div />
                <button className="signup-btn primary" onClick={() => setStep(2)} data-testid="next-step-1">
                  Continue <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Details */}
          {step === 2 && (
            <div className="signup-step-content" data-testid="step-details">
              <h2>Your Information</h2>
              <p className="signup-subtitle">We'll create your portal account after payment.</p>
              {error && <div className="signup-error" data-testid="signup-error"><AlertCircle size={16} /> {error}</div>}
              <div className="signup-form">
                <div className="signup-row">
                  <div className="signup-field">
                    <label>First Name *</label>
                    <div className="signup-input-wrap">
                      <User size={16} />
                      <input type="text" name="firstName" value={formData.firstName} onChange={handleInputChange} placeholder="First name" data-testid="input-firstname" />
                    </div>
                  </div>
                  <div className="signup-field">
                    <label>Last Name *</label>
                    <div className="signup-input-wrap">
                      <User size={16} />
                      <input type="text" name="lastName" value={formData.lastName} onChange={handleInputChange} placeholder="Last name" data-testid="input-lastname" />
                    </div>
                  </div>
                </div>
                <div className="signup-field">
                  <label>Email *</label>
                  <div className="signup-input-wrap">
                    <Lock size={16} />
                    <input type="email" name="email" value={formData.email} onChange={handleInputChange} placeholder="you@company.com" data-testid="input-email" />
                  </div>
                </div>
                <div className="signup-field">
                  <label>Company *</label>
                  <div className="signup-input-wrap">
                    <Building2 size={16} />
                    <input type="text" name="company" value={formData.company} onChange={handleInputChange} placeholder="Company name" required data-testid="input-company" />
                  </div>
                </div>
              </div>
              <div className="signup-actions">
                <button className="signup-btn outline" onClick={() => setStep(1)} data-testid="back-step-2">
                  <ChevronLeft size={16} /> Back
                </button>
                <button className="signup-btn primary" onClick={handleCheckout} disabled={loading} data-testid="proceed-to-payment">
                  {loading ? 'Redirecting to payment...' : <><CreditCard size={16} /> Proceed to Payment</>}
                </button>
              </div>
              <p className="signup-secure-note"><Lock size={13} /> Secure checkout powered by Stripe</p>
            </div>
          )}
        </div>

        <p className="signup-footer-text">
          Already have an account? <Link to="/portal/login" data-testid="login-link">Log in</Link>
        </p>
      </div>
      <Footer />
    </div>
  );
};

export default PortalSignup;
